import React from 'react';
import { getYouTubeThumbnail } from '../../../../lib/videoUtils';
import { ProgressIndicator } from './ProgressIndicator';

interface FileGridItemShellProps {
  /**
   * File name/title
   */
  name: string;
  /**
   * Thumbnail URL or preview URL
   */
  thumbnailUrl?: string;
  /**
   * YouTube URL for imports
   */
  youtubeUrl?: string;
  /**
   * Status text to display at bottom
   */
  statusText: string;
  /**
   * Upload/import progress (0-100) or undefined for indeterminate spinner
   */
  progress?: number;
  /**
   * Whether to show percentage text with progress
   */
  showPercentage?: boolean;
  /**
   * Optional content to render in the body
   */
  children?: React.ReactNode;
  /**
   * File type for preview rendering
   */
  fileType?: string;
}

/**
 * Unified grid view item shell used for uploads, imports, and processing files
 * Provides consistent styling and structure across all grid view items
 */
export const FileGridItemShell: React.FC<FileGridItemShellProps> = ({
  name,
  thumbnailUrl,
  youtubeUrl,
  statusText,
  progress,
  showPercentage = false,
  children,
  fileType,
}) => {
  const isVideo = fileType?.startsWith('video/');
  const isAudio = fileType?.startsWith('audio/');
  const ytThumbnail = getYouTubeThumbnail(youtubeUrl);
  const displayThumbnail = ytThumbnail || thumbnailUrl;

  // For audio files, don't show thumbnail - just white background with progress
  const shouldShowThumbnail = !isAudio && displayThumbnail;

  return (
    <div className="relative flex flex-col h-full border border-border rounded-lg overflow-hidden bg-white dark:bg-background opacity-70 hover:opacity-80 transition-opacity cursor-not-allowed">
      {/* Thumbnail area with loader overlay */}
      <div className="relative">
        {shouldShowThumbnail ? (
          <div className="w-full aspect-video bg-gray-50 dark:bg-gray-900/50 relative">
            {isVideo && thumbnailUrl ? (
              <video
                src={thumbnailUrl}
                className="w-full h-full object-cover"
                muted
              />
            ) : (
              <img
                src={displayThumbnail}
                alt={name}
                className="w-full h-full object-cover"
              />
            )}
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ) : (
          <div className="w-full aspect-video bg-white dark:bg-white" />
        )}
        {/* Progress indicator overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <ProgressIndicator
            progress={progress}
            size="lg"
            variant={shouldShowThumbnail ? 'dark' : 'light'}
            showPercentage={showPercentage}
          />
        </div>
      </div>

      {/* Content area */}
      <div className="p-3 flex-grow flex flex-col">
        <h3 className="text-sm font-medium line-clamp-2 leading-snug mb-1.5 min-h-[2.5rem] text-gray-700 dark:text-gray-300">
          {name}
        </h3>
        <div className="mt-auto">
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {statusText}
          </p>
        </div>
        {children}
      </div>
    </div>
  );
};
